import { GetStaticPaths, GetStaticProps } from 'next';
import { compact } from 'lodash-es';

import { Box, Flex } from '@chakra-ui/react';
import { Certificate as CertificateComponent } from '@udacity/udacity-certificate';

import { ContentContainer } from '~/components/container';
import { PageCategory } from '~/features/analytics/constants';
import { MarketingMessage } from '~/features/certificates/components/marketing-message';
import { getCertificateByToken } from '~/features/certificates/controllers/get-certificate';
import { Certificate } from '~/features/certificates/model';
import { getCertificateProgramTypeFromSemanticType, validateToken } from '~/features/certificates/utils';
import { DEFAULT_LANGUAGE } from '~/features/internationalization/languages';
import { PageHead } from '~/features/page/components/page-head';
import { PageLayout } from '~/features/page/components/page-layout';
import { Page } from '~/features/page/models/page';
import { getInternalPage } from '~/features/page/utils/get-internal-page';
import { getProgramContent } from '~/features/program/controllers/get-program-content';
import { ProgramContent } from '~/features/program/models/program-content';

export default function LegacyCertificatePage({
  page,
  fullName,
  certificate,
  programSlug,
}: {
  page: Page;
  fullName: string;
  certificate: Certificate;
  programSlug: string | null;
}) {
  const programType = certificate.semanticType
    ? getCertificateProgramTypeFromSemanticType(certificate.semanticType)
    : undefined;

  return (
    <>
      <style>
        {`
          @media print{
            html, body, #__next {
              overflow: hidden; !important
            }
          }
        `}
      </style>
      <PageHead page={page} pageCategory={PageCategory.Uncategorized} />
      <PageLayout isSimpleMode>
        <ContentContainer>
          <Flex
            gap='24px'
            flexDir={{
              base: 'column',
              md: 'row',
            }}
          >
            <Box flex='1'>
              <CertificateComponent
                companyLogo={certificate.companyLogo}
                confirmLink={certificate.confirmUrl}
                contract={certificate.contract}
                fullName={fullName}
                graduationDate={certificate.graduationDate}
                partnerLogos={certificate.partnerLogos}
                pdfUrl={certificate.pdfUrl}
                programTitle={certificate.programTitle}
                programType={programType}
                svgUrl={certificate.svgUrl}
              />
            </Box>
            <Box flex='0 2 360px'>
              <MarketingMessage programSlug={programSlug} programTitle={certificate.programTitle} />
            </Box>
          </Flex>
        </ContentContainer>
      </PageLayout>
    </>
  );
}

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking',
  };
};

export const getStaticProps: GetStaticProps = async ({ locale, params }) => {
  // Stop additional locale pages except the default locale from being built
  if (locale !== DEFAULT_LANGUAGE) {
    return {
      notFound: true,
    };
  }

  const token = params?.token;

  if (!token || !validateToken(token as string)) {
    return {
      notFound: true,
    };
  }

  let certificate: Certificate | undefined = undefined;

  try {
    certificate = await getCertificateByToken(token as string);
  } catch {
    return {
      notFound: true,
    };
  }

  if (!certificate) {
    return {
      notFound: true,
    };
  }

  const page = getInternalPage({ eventName: 'Certificate Page Viewed', nofollow: false });

  const fullName = compact([certificate?.firstName, certificate?.lastName]).join(' ');

  let programContent: ProgramContent | undefined = undefined;

  if (certificate.programKey) {
    programContent = await getProgramContent(certificate.programKey);
  }

  const programSlug = programContent?.slug || null;

  return {
    props: {
      page,
      fullName,
      certificate,
      programSlug,
    },
    revalidate: false,
  };
};
